import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'design-system';
import { FileDownIcon } from 'lucide-react';

import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import downloadFile from '@/shared/lib/download/downloadFile';

import { filesWidgetOptions } from './api/filesWidget.options';

/* oxlint-disable jsx-a11y/no-noninteractive-tabindex -- Truncated filename tooltip triggers need keyboard focus without implying a button action. */

type FilesWidgetProps = {
  groupName: string | undefined;
  size?: number;
  downloadable?: string;
};

const FilesWidgetContent = ({ groupName, size, downloadable }: FilesWidgetProps) => {
  const { hasPermission } = usePermission();
  const { data, isLoading, isError } = useQuery(
    filesWidgetOptions.files(groupName, size, hasPermission(Permission.ViewMarketScreens))
  );

  if (!groupName) {
    return (
      <Alert>
        <AlertTitle>Files unavailable</AlertTitle>
        <AlertDescription>File group is required.</AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div
        aria-label='Loading files'
        className='space-y-2 p-4'
      >
        <Skeleton className='h-8 w-full' />
        <Skeleton className='h-8 w-full' />
        <Skeleton className='h-8 w-full' />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert>
        <AlertTitle>Files unavailable</AlertTitle>
        <AlertDescription>Failed to load files.</AlertDescription>
      </Alert>
    );
  }

  if (!data?.length) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>No files available.</EmptyTitle>
          <EmptyDescription>Files published to this group will appear here.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <Table
      aria-label='Market files'
      className='table-fixed'
    >
      <TableHeader>
        <TableRow>
          <TableHead className='w-auto'>File name</TableHead>
          <TableHead className='w-[120px]'>Size</TableHead>
          <TableHead className='w-[120px]'>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map(file => (
          <TableRow key={file.fid}>
            <TableCell className='overflow-hidden'>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <span
                      tabIndex={0}
                      className='block max-w-full truncate rounded-sm outline-none focus-visible:ring-3 focus-visible:ring-ring/50'
                    >
                      {file.name}
                    </span>
                  }
                />
                <TooltipContent>{file.name}</TooltipContent>
              </Tooltip>
            </TableCell>
            <TableCell>{file.size}</TableCell>
            <TableCell>
              <IconButton
                disabled={downloadable === 'false'}
                label={`Download ${file.name}`}
                onClick={() => {
                  downloadFile(file.fid, file.name).catch(() => {
                    // downloadFile already retries and logs failures; keep this flow fire-and-forget.
                  });
                }}
              >
                <FileDownIcon aria-hidden />
              </IconButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const FilesWidget = ({ groupName, size, downloadable }: FilesWidgetProps) => {
  const { hasPermission } = usePermission();

  if (!hasPermission(Permission.ViewMarketScreens)) {
    return (
      <Alert>
        <AlertTitle>Restricted access</AlertTitle>
        <AlertDescription>Upgrade your plan to view market files.</AlertDescription>
      </Alert>
    );
  }

  return (
    <FilesWidgetContent
      groupName={groupName}
      size={size}
      downloadable={downloadable}
    />
  );
};

export default FilesWidget;
